import * as Sentry from '@sentry/vue'
import { markRaw } from 'vue'
import { useBottomDrawer } from '@/composables/useBottomDrawer'
import { useUserStore } from '@/stores/userStore'

// Global singleton state for widget management - synced with plugin
const globalWidgetState = {
  instance: null as any,
  isVisible: false
}

// Make state accessible globally for plugin sync
if (import.meta.client) {
  (window as any).__sentryWidgetState = globalWidgetState
}

export const useSentryFeedback = () => {
  const bottomDrawer = useBottomDrawer()
  const userStore = useUserStore()
  const route = useRoute()
  

  // Get the Sentry feedback instance
  const feedback = Sentry.getFeedback()

  // Use global widget state instead of local refs
  const widgetInstance = computed({
    get: () => globalWidgetState.instance,
    set: (value) => { globalWidgetState.instance = value }
  })
  const isWidgetVisible = computed({
    get: () => globalWidgetState.isVisible,
    set: (value) => { globalWidgetState.isVisible = value }
  })

  // Check if feedback should be shown on current route
  const shouldShowFeedback = computed(() => {
    return route.path !== '/'
  })

  const setSentryUser = (user: any) => {
    if (user) {
    Sentry.setUser({
      fullName: user?.name || user?.user_metadata?.name,
      email: user?.email,
      name: user?.name || user?.user_metadata?.name
      })
    }
  }

  /**
   * Method 1: Use Sentry's default widget with custom button
   * This shows the Sentry form but triggered by our own button
   */
  const openSentryWidget = () => {
    if (!shouldShowFeedback.value) {
      
      return
    }

    if (!feedback) {
      console.warn('Sentry feedback not available')
      return
    }

    // Don't create a new widget if one already exists and is visible
    if (isWidgetVisible.value && widgetInstance.value) {
      
      return
    }

    // Set user context before opening
    if (userStore.user) {
      setSentryUser(userStore.user)
    }

    // Create and show the widget only if we don't have one
    if (!widgetInstance.value) {
      try {
        widgetInstance.value = feedback.createWidget()
        widgetInstance.value?.appendToDom()
        isWidgetVisible.value = true
        
      } catch (error) {
        console.error('Failed to create Sentry widget:', error)
      }
    }
  }

  const hideSentryWidget = () => {
    if (!feedback) return

    try {
      // Remove the tracked widget instance first
      if (widgetInstance.value) {
        widgetInstance.value.removeFromDom?.()
        widgetInstance.value = null
      }
      
      // Remove any existing widget instances using Sentry's API
      feedback.remove()
      
      // Also try to remove any widget elements that might still be in the DOM
      const widgetElements = document.querySelectorAll('#superseeded-feedback, .sentry-feedback-widget, [id^="sentry-feedback"]')
      widgetElements.forEach(el => el.remove())
      
      // Update visibility state
      isWidgetVisible.value = false
      
      
    } catch (error) {
      console.warn('Error hiding Sentry widget:', error)
    }
  }

  /**
   * Method 2: Attach feedback to an existing button element
   */
  const attachToButton = (buttonElement: HTMLElement, customOptions?: any) => {
    if (!shouldShowFeedback.value) {
      
      return
    }

    if (!feedback) {
      console.warn('Sentry feedback not available')
      return
    }

    // Set user context
    if (userStore.user) {
      Sentry.setUser({
        email: userStore.user.email,
        name: userStore.user.name || userStore.user.email,
        id: userStore.user.id
      })
    }

    feedback.attachTo(buttonElement, {
      formTitle: "Share Your SuperSeeded Experience",
      submitButtonLabel: "Send to SuperSeeded Team",
      ...customOptions
    })
  }

  /**
   * Method 3: Create a custom form with full control
   */
  const openCustomForm = async () => {
    if (!shouldShowFeedback.value) {
      
      return
    }

    if (!feedback) {
      console.warn('Sentry feedback not available')
      return
    }

    // Set user context
    if (userStore.user) {
      Sentry.setUser({
        email: userStore.user.email,
        name: userStore.user.name || userStore.user.email,
        id: userStore.user.id
      })
    }

    try {
      const form = await feedback.createForm()
      form?.appendToDom()
      form?.open()
    } catch (error) {
      console.error('Failed to create feedback form:', error)
    }
  }

  /**
   * Method 4: Completely custom widget using captureFeedback
   * This bypasses Sentry's UI entirely and sends data directly
   */
  const sendCustomFeedback = (message: string, options?: {
    name?: string
    email?: string
    attachments?: Array<{ filename: string; data: string }>
    tags?: Record<string, string>
    includeReplay?: boolean
  }) => {
    const feedbackData = {
      message,
      name: options?.name || userStore.user?.name || userStore.user?.email || 'Anonymous',
      email: options?.email || userStore.user?.email || '',
    }

    const hints = {
      includeReplay: options?.includeReplay ?? true,
      attachments: options?.attachments || [],
      tags: {
        source: 'custom-widget',
        userType: userStore.user?.role || 'unknown',
        ...options?.tags
      }
    }

    try {
      Sentry.captureFeedback(feedbackData, hints)
      return true
    } catch (error) {
      console.error('Failed to send feedback:', error)
      return false
    }
  }

  /**
   * Helper to show feedback in bottom drawer with our custom component
   */
  const openInDrawer = () => {
    if (!shouldShowFeedback.value) {
      
      return
    }

    bottomDrawer.open({
      title: 'Send Feedback to SuperSeeded',
      description: 'Help us improve your experience',
      hideCloseButton: false,
      component: markRaw(defineAsyncComponent(() => import('@/components/SentryFeedbackWidget.vue'))),
      props: {
        onSubmit: (success: boolean) => {
          if (success) {
            bottomDrawer.close()
          }
        }
      }
    })
  }

  return {
    // Direct Sentry methods
    openSentryWidget,
    attachToButton,
    openCustomForm,
    setSentryUser,
    // Custom implementations
    sendCustomFeedback,
    openInDrawer,
    hideSentryWidget,
    // Utility
    isAvailable: !!feedback,
    shouldShowFeedback,
    isWidgetVisible
  }
} 